import {
  LayoutGrid,
  TypographyRhythm,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';

export interface FeatureProps {
  readonly title: MessageKey;
  readonly body: JSX.Element | ReactNode | string;
  readonly image: ImageProps;
  readonly overlayImage?: ImageProps;
  readonly reverse?: boolean;
  readonly button?: JSX.Element | null;
}

const Feature = ({
  title,
  body,
  image,
  overlayImage,
  reverse,
  button = null,
}: FeatureProps): JSX.Element => {
  const t = useTranslations();

  return (
    <Motion
      className="py-5 md:py-10"
      initial={{ opacity: 0, y: 30 }}
      transition={{ delay: 0.1, duration: 0.5, type: 'tween' }}
      viewport={{ amount: 0.333, once: true }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      <LayoutGrid>
        <div
          className={cx(
            'row-start-2 mx-auto flex max-w-md flex-col items-start justify-center md:row-start-1 lg:max-w-none',
            {
              'col-start-2 col-end-8 md:col-start-13 md:col-end-22': reverse,
              'col-start-4 col-end-main md:col-start-4 md:col-end-13': !reverse,
            },
          )}
        >
          <TypographyRhythm>
            <TypographyV3 Element="h3" variant="h4" className="mb-4">
              {t(title)}
            </TypographyV3>
            {body}
          </TypographyRhythm>
          {button}
        </div>
        <div
          className={cx(
            'relative row-start-1 mx-auto flex max-w-md flex-col items-start justify-center lg:max-w-none',
            {
              'col-start-full col-end-8 md:col-end-11': reverse,
              'col-start-4 col-end-full md:col-start-15': !reverse,
            },
          )}
        >
          <Image {...image} />
          {overlayImage && (
            <div
              className={cx(
                'absolute top-1/2 h-[55vw] w-1/2 md:h-[32vw] md:max-h-[465px]',
                {
                  '-right-14 lg:-right-16': reverse,
                  '-left-1/4': !reverse,
                },
              )}
            >
              <Image {...overlayImage} />
            </div>
          )}
        </div>
      </LayoutGrid>
    </Motion>
  );
};

export default Feature;
